import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { ProductMarginRow } from '@erp/rpc';

/**
 * Bulk-loads margin rows for a set of product variant ids. Result is exposed
 * as a Map keyed by productVariantId so cell renderers can do O(1) lookups
 * and render "—" when a variant id has no entry (e.g. unmatched line item).
 *
 * Pass `enabled: false` when the margin view is hidden to skip the network
 * round trip entirely.
 *
 * Pass `customerId` to scope the sales history (ASP / qty sold) to customers
 * sharing that customer's price list — the distributor's region — instead of
 * the whole organization. `salesScope` reports whether the scoping applied.
 */
export function useGetProductMarginsByVariantIds(
  productVariantIds: string[],
  {
    enabled = true,
    customerId,
  }: { enabled?: boolean; customerId?: string } = {},
) {
  // Stable sorted list so the query key doesn't depend on input order.
  const sortedIds = useMemo(
    () => [...new Set(productVariantIds)].sort(),
    [productVariantIds],
  );

  const query = useQuery({
    ...orpc.inventoryManagement.productMargins.byVariantIds.queryOptions({
      input: { productVariantIds: sortedIds, customerId },
    }),
    enabled: enabled && sortedIds.length > 0,
  });

  const marginByVariantId = useMemo(() => {
    const map = new Map<string, ProductMarginRow>();
    for (const row of query.data?.rows ?? []) {
      map.set(row.productVariantId, row);
    }
    return map;
  }, [query.data]);

  return {
    marginByVariantId,
    hasProductMargins: query.data?.hasProductMargins ?? false,
    costCurrency: query.data?.costCurrency ?? ('USD' as 'USD' | 'EUR'),
    conversionApplied: query.data?.conversionApplied ?? false,
    yearsCovered: query.data?.yearsCovered ?? [],
    salesScope: query.data?.salesScope ?? ('organization' as const),
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
}
